import { Injectable } from '@angular/core';
import { CidaasService } from './cidaas.service';
// TODO: Remove lodash
import * as _ from 'lodash';
import {KEY_DEFAULT_ERROR} from '../model/error.model';

interface CustomFields {
  value: string | string[];
}

interface IRegisterResponse {
  email_verified?: boolean;
  password_reset_link?: string;
  q?: string;
  sub?: string;
  suggested_action?: string;
  suggested_redirect_uri?: string;
  action?: string;
  redirect_uri?: string;
  userStatus?: string;
  trackId?: string;
  rememberMe?: boolean;
  lat?: string;
  lon?: string;
}

enum RegisterActions {
  Register_Success = 'REGISTER_SUCCESS',
  Deduplication = 'DEDUPLICATION',
  Verification = 'VERIFICATION',
  Login = 'Login',
  Auto_Login = 'AUTO_LOGIN',
}

@Injectable({
  providedIn: 'root',
})
export class RegistrationService {
  constructor(private _cidaasSDK: CidaasService) {}

  updateValue(data: any[], inviteUserDetails): any {
    // TODO: Do make immutable transformation
    const findEmail = _.find(data, (field: any) => field.fieldKey === 'email');
    if (findEmail && inviteUserDetails.invited_email) {
      findEmail.value = inviteUserDetails.invited_email;
      if (inviteUserDetails.allow_same_email) {
        findEmail.readOnly = inviteUserDetails.allow_same_email;
      }
    }
    const findFamilyName = _.find(data, (field: any) => field.fieldKey === 'family_name');
    if (findFamilyName && inviteUserDetails.invited_family_name) {
      findFamilyName.value = inviteUserDetails.invited_family_name;
    }
    const findGivenName = _.find(data, (field: any) => field.fieldKey === 'given_name');
    if (findGivenName && inviteUserDetails.invited_name) {
      findGivenName.value = inviteUserDetails.invited_name;
    }
    const findMobileNumber = _.find(data, (field: any) => field.fieldKey === 'mobile_number');
    if (findMobileNumber && inviteUserDetails.mobile_number) {
      findMobileNumber.value = inviteUserDetails.mobile_number;
    }
    data.forEach((field) => {
      if (field.fieldType === 'CUSTOM' && inviteUserDetails.customFields) {
        Object.keys(inviteUserDetails.customFields).forEach((customFields) => {
          if (field.fieldKey === customFields) {
            switch (field.dataType) {
              case 'MULTISELECT':
                if (
                  (inviteUserDetails.customFields[customFields].value && inviteUserDetails.customFields[customFields].value.length > 0) ||
                  (inviteUserDetails.customFields[customFields] && inviteUserDetails.customFields[customFields].length > 0)
                ) {
                  field.value = [];
                  const values = this.getValue(inviteUserDetails.customFields[customFields]);
                  field.localeText.attributes.forEach((attr) => {
                    if (this.isArray(values)) {
                      values.forEach((val) => {
                        if (attr.key === val || attr.value === val) {
                          field.value.push(attr.key);
                        }
                      });
                    }
                  });
                }
                break;
              case 'SELECT':
              case 'RADIO':
                const selectedValue = this.getValue(inviteUserDetails.customFields[customFields]);
                field.localeText.attributes.forEach((attr) => {
                  if (attr.key === selectedValue || attr.value === selectedValue) {
                    field.value = attr.key;
                  }
                });
                break;
              default:
                field.value = this.getValue(inviteUserDetails.customFields[customFields]);
                break;
            }
          }
        });
      }
    });
    return data;
  }

  isArray(value: string | string[] | CustomFields): value is string[] {
    return (value as string[]).forEach !== undefined;
  }

  getValue = (customFields: CustomFields): string | string[] | CustomFields => {
    if (customFields.value) {
      return customFields.value;
    }
    if (customFields.value === '') {
      return '';
    }
    return customFields;
  };

  getErrorMessage(errorResponse): string {
    if (errorResponse.status === 409) {
      return 'CONFLICT_REGISTER';
    } else if (errorResponse && errorResponse.code === 10028) {
      return 'IP_BLOCKED';
    } else if (errorResponse && errorResponse.error === 'invalid mobile_number passed') {
      return 'ENTER_VALID_MOBILE';
    } else if (errorResponse && errorResponse.error === 'invalid email passed') {
      return 'ENTER_VALID_EMAIL';
    } else if (errorResponse && errorResponse.error === 'Captcha validation fails') {
      return 'CAPTCHA_REQUIRED';
    } else if (errorResponse && errorResponse.error === 'Invalid Captcha response passed') {
      return 'CAPTCHA_REQUIRED';
    } else {
      return KEY_DEFAULT_ERROR;
    }
  }

  // checks whether all fields have proper translations.
  // if not it will return false
  _validateFormData = (data: any, returnFields: boolean = false) => {
    if (!returnFields) {
      return data.filter((x) => {
        return !x?.localeText;
      }).length > 0
        ? false
        : true;
    }
    return data
      .filter((x) => {
        return !x?.localeText;
      })
      .map((x) => {
        return x.fieldKey;
      });
  };

  /**
   *
   * @param data:IRegisterResponse
   *
   * The mothod will handle the register redirection according to the `action` and `redirect_uri`
   */
  _handleRegisterSuccessRedirection = (data: IRegisterResponse): void => {
    if (data?.redirect_uri && this._authenticateRedirectURI(data?.redirect_uri, data?.action)) {
      // we will perform the auto login if the action is AUTO_LOGIN else will redirect to the provided url
      if (data?.action === RegisterActions.Auto_Login) { this._cidaasSDK._loginAfterRegister(data) } else { window.location.href = data?.redirect_uri; }

    } else {
      // handling the auto_login functionality if there is no redirect_uri present
      if (data?.action === RegisterActions.Auto_Login) {
        this._cidaasSDK._loginAfterRegister(data);
      } else {
        console.warn('Cidaas: redirection is not possible due to missing queryparams!');
      }
    }
  };

  /**
   *
   * @param uri
   * @param action
   * @returns true/false
   *
   * The method will authenticate the given uri is valid or not
   */
  _authenticateRedirectURI = (uri: string, action: string = 'REGISTER_SUCCESS'): boolean => {
    switch (action) {
      case RegisterActions.Deduplication:
        return this._checkQueryParams(['trackId', 'requestId', 'provider'], uri);
        break;
      case RegisterActions.Verification:
        return this._checkQueryParams(['verification_type', 'requestId'], uri);
        break;
      case RegisterActions.Register_Success:
        return this._checkQueryParams(['requestId'], uri);
        break;
      case RegisterActions.Auto_Login:
        return this._checkQueryParams(['requestId', 'trackId'], uri);
        break;
      default:
        return this._checkQueryParams(['requestId'], uri);
        break;
    }
  };

  /**
   *
   * @param params
   * @param url
   * @returns boolean
   *
   * It will check whether the url has the parameters which we mentioned
   */
  _checkQueryParams = (params: string[], url: string): boolean => {
    const searchparams = new URLSearchParams(url.substring(url.indexOf('?') + 1));
    return params.filter((x: string) => {
      return !searchparams.has(x);
    }).length
      ? false
      : true;
  };
}
